import React, { useState, useEffect } from 'react';
import DateInput from './DateInput';
import { AgGridReact } from 'ag-grid-react';
import Highcharts from 'highcharts/highstock';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import { ColDef } from 'ag-grid-community';
import { useGridCopyMenu } from './gridCommon';
import { callAxiosGet } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';

// Highcharts 접근성 모듈 초기화
HighchartsAccessibility(Highcharts);

interface TopRiseStock {
  prodId: string;
  prodNm?: string;
  openprc: number;
  highprc: number;
  lowprc: number;
  clsprc: number;
  qty: number;
  amt: number;
  diff?: number;
  flurt?: number;
  ind?: number;
  orgn?: number;
  frgn?: number;
}

interface StockHistory {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

const TopRiseRate: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const [selectedDate, setSelectedDate] = useState('2025-01-14');
  const [topRiseStocks, setTopRiseStocks] = useState<TopRiseStock[]>([]);
  const [selectedStock, setSelectedStock] = useState<TopRiseStock | null>(null);
  const [stockHistory, setStockHistory] = useState<StockHistory[]>([]);
  const [loading, setLoading] = useState(false);

  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();

  // 상승률 상위 100개 종목 조회
  const fetchTopRiseStocks = (date: string) => {
    callAxiosGet({
      axiosInstance,
      m,
      logger,
      url: '/api/v1/stocks/top-rise-rate',
      params: { d: date },
      onSuccess: (data) => {
        setTopRiseStocks(data as TopRiseStock[]);
      },
    });
  };

  useEffect(() => {
    fetchTopRiseStocks(selectedDate);
  }, [selectedDate]);

  // 선택된 종목의 1년 히스토리 조회
  const fetchStockHistory = (prodId: string) => {
    setLoading(true);
    callAxiosGet({
      axiosInstance,
      m,
      logger,
      url: `/api/v1/stocks/${prodId}/history`,
      params: {},
      onSuccess: (data) => {
        setStockHistory(data as StockHistory[]);
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
      },
    });
  };

  // AG Grid 컬럼 정의
  const columnDefs: ColDef<TopRiseStock>[] = [
    {
      field: 'prodId',
      headerName: '종목코드',
      width: 120,
      cellStyle: { textAlign: 'left' },
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'prodNm',
      headerName: '종목명',
      width: 150,
      cellStyle: { textAlign: 'left' },
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'clsprc',
      headerName: '종가',
      width: 100,
      valueFormatter: (params) => params.value?.toLocaleString() || '',
      cellStyle: { textAlign: 'right' },
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'openprc',
      headerName: '시가',
      width: 100,
      valueFormatter: (params) => params.value?.toLocaleString() || '',
      cellStyle: { textAlign: 'right' },
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'highprc',
      headerName: '고가',
      width: 100,
      valueFormatter: (params) => params.value?.toLocaleString() || '',
      cellStyle: { textAlign: 'right' },
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'lowprc',
      headerName: '저가',
      width: 100,
      valueFormatter: (params) => params.value?.toLocaleString() || '',
      cellStyle: { textAlign: 'right' },
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'diff',
      headerName: '전일대비',
      width: 100,
      valueFormatter: (params) => params.value != null ? params.value.toLocaleString() : '',
      cellStyle: (params) => ({
        textAlign: 'right',
        color: params.value > 0 ? '#dc2626' : params.value < 0 ? '#2563eb' : '#000',
      }),
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'flurt',
      headerName: '등락률 (%)',
      width: 110,
      valueFormatter: (params) => params.value != null ? params.value.toFixed(2) : '',
      cellStyle: (params) => ({
        textAlign: 'right',
        color: params.value > 0 ? '#dc2626' : params.value < 0 ? '#2563eb' : '#000',
      }),
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'qty',
      headerName: '거래량',
      width: 120,
      valueFormatter: (params) => params.value?.toLocaleString() || '',
      cellStyle: { textAlign: 'right' },
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'amt',
      headerName: '거래대금 (원)',
      width: 150,
      valueFormatter: (params) => params.value?.toLocaleString() || '',
      cellStyle: { textAlign: 'right' },
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'ind',
      headerName: '개인',
      width: 120,
      valueFormatter: (params) => params.value?.toLocaleString() || '',
      cellStyle: { textAlign: 'right' },
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'orgn',
      headerName: '기관',
      width: 120,
      valueFormatter: (params) => params.value?.toLocaleString() || '',
      cellStyle: { textAlign: 'right' },
      headerStyle: { textAlign: 'center' }
    },
    {
      field: 'frgn',
      headerName: '외국계',
      width: 120,
      valueFormatter: (params) => params.value?.toLocaleString() || '',
      cellStyle: { textAlign: 'right' },
      headerStyle: { textAlign: 'center' }
    },
  ];

  // 행 클릭 핸들러
  const onRowClicked = (event: any) => {
    const stock = event.data as TopRiseStock;
    setSelectedStock(stock);
    fetchStockHistory(stock.prodId);
  };

  // Highcharts 옵션
  const chartOptions: Highcharts.Options = {
    title: {
      text: selectedStock ? `${selectedStock.prodId} - 1년 차트` : '종목을 선택하세요',
      style: {
        fontSize: '14px'
      }
    },
    plotOptions: {
      series: {
        dataGrouping: {
          enabled: false
        }
      }
    },
    xAxis: {
      type: 'datetime',
      labels: {
        style: {
          fontSize: '11px'
        }
      },
      title: {
        style: {
          fontSize: '11px'
        }
      }
    },
    yAxis: [
      {
        title: { 
          text: '주가',
          style: {
            fontSize: '11px'
          }
        },
        height: '70%',
        labels: {
          style: {
            fontSize: '11px'
          }
        }
      },
      {
        title: { 
          text: '거래량',
          style: {
            fontSize: '11px'
          }
        },
        top: '75%',
        height: '25%',
        offset: 0,
        labels: {
          style: {
            fontSize: '11px'
          }
        }
      }
    ],
    series: [
      {
        type: 'candlestick',
        name: '주가',
        data: stockHistory.map(h => [
          new Date(h.date).getTime(),
          h.open,
          h.high,
          h.low,
          h.close
        ]),
        yAxis: 0
      },
      {
        type: 'column',
        name: '거래량',
        data: stockHistory.map(h => [
          new Date(h.date).getTime(),
          h.volume
        ]),
        yAxis: 1
      }
    ],
    rangeSelector: {
      enabled: true,
      selected: 4,
      buttons: [
        { type: 'month', count: 1, text: '1개월' },
        { type: 'month', count: 3, text: '3개월' },
        { type: 'month', count: 6, text: '6개월' },
        { type: 'year', count: 1, text: '1년' },
        { type: 'all', text: '전체' }
      ],
      inputEnabled: false,
      buttonPosition: {
        align: 'right',
        x: -10
      },
      buttonTheme: {
        style: {
          fontSize: '11px'
        }
      }
    },
    legend: {
      itemStyle: {
        fontSize: '11px'
      }
    },
    navigator: {
      enabled: true
    },
    scrollbar: {
      enabled: true
    },
    credits: {
      enabled: false
    }
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 날짜 선택 및 조회 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="rise-date-picker" style={{ fontWeight: 'bold', color: '#1e40af', fontSize: '12px', whiteSpace: 'nowrap' }}>조회일자:</label>
            <DateInput
              id="rise-date-picker"
              value={selectedDate}
              onChange={(v) => setSelectedDate(v)}
              style={{ border: '2px solid #3b82f6' }}
            />
          </div>
          <button
            onClick={() => fetchTopRiseStocks(selectedDate)}
            style={{
              padding: '5px 12px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            조회
          </button>
        </div>
      </div>

      {/* AG Grid */}
      <div style={{
        height: '400px',
        width: '100%',
        marginBottom: '20px',
        backgroundColor: 'white',
        padding: '10px',
        borderRadius: '8px',
        border: '2px solid #3b82f6',
        boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)',
        fontSize: '12px'
      }} className="ag-theme-alpine">
        <AgGridReact {...copyMenu.gridProps}
          rowData={topRiseStocks}
          columnDefs={columnDefs}
          onRowClicked={onRowClicked}
          domLayout="normal"
          defaultColDef={{
            flex: 1,
            minWidth: 100,
            resizable: true,
            sortable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { fontSize: '12px' },
            headerStyle: { fontSize: '12px', textAlign: 'center' }
          }}
        />
      </div>

      {/* Highcharts */}
      {selectedStock && (
        <div style={{
          marginTop: '20px',
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '2px solid #3b82f6',
          boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: '#1e40af' }}>차트 로딩 중...</p>
            </div>
          ) : (
            <HighchartsReact
              highcharts={Highcharts}
              constructorType={'stockChart'}
              options={chartOptions}
            />
          )}
        </div>
      )}

      {!selectedStock && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          border: '2px dashed #3b82f6',
          borderRadius: '8px',
          marginTop: '20px',
          backgroundColor: 'white'
        }}>
          <p style={{ color: '#1e40af', fontSize: '16px' }}>
            위 테이블에서 종목을 클릭하면 1년 차트가 표시됩니다
          </p>
        </div>
      )}
      {copyMenu.menu}
    </div>
  );
};

export default TopRiseRate;

